import {
  ActionIcon,
  Alert,
  Anchor,
  Box,
  Button,
  Collapse,
  Group,
  Loader,
  ScrollArea,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { IconAlertCircle, IconCheck, IconChevronDown, IconChevronUp, IconRefresh, IconSend } from '@tabler/icons-react';
import { useMedplum } from '@medplum/react-hooks';
import { useEffect, useRef, useState } from 'react';

import { ChatMessageBubble } from './ChatMessageBubble';
import { useScrollLatestChatToTop } from './useScrollLatestChatToTop';

import {
  adaptLegacySuggestedResponsesToChecklist,
  type ChatChecklistPart,
  type ChatChecklistSubmission,
} from './chat-artifacts/chatMessageParts';
import { ChatChecklistCard } from './chat-artifacts/ChatChecklistCard';
import {
  requestAiTreatmentPlanGenerator,
  type TreatmentPlanGeneratorMessage,
  type TreatmentPlanGeneratorMode,
} from '../../lib/aiTreatmentPlanGeneratorService';
import {
  mergeEvidenceGroundedSymptoms,
  symptomsFromGuidedChecklistLabels,
  type EvidenceGroundedSymptom,
} from '../../lib/treatment-plan/evidenceGroundedSymptoms';
import { formatMedplumClientError } from '../../pages/appointments/utils/medplumClientErrors';
import type { TreatmentPlanGuidedContext } from './types';

type Busy = 'chat' | 'generate' | null;

const QUICK_MAX_PROVIDER_TURNS = 3;
/**
 * Survives React StrictMode remounts (which reset component refs). Keyed by the drawer
 * `launchNonce` so a fresh START_QUICK still kicks off once.
 */
const quickKickoffsStarted = new Set<string>();
const QUICK_KICKOFF_MESSAGE =
  'Use existing chart documentation (signed intake note and Zoom intake transcript when available). '
  + 'Ask only for missing essentials — ideally one confirmation checklist — then generate.';
const DIAGNOSIS_REQUIRED_MESSAGE =
  'Before we can build this treatment plan together, select a diagnosis in the treatment plan '
  + 'form. I will continue automatically once a diagnosis is selected — you do not need to reopen '
  + 'the planner.';

/** Stable fingerprint of form diagnoses so mid-session code edits are detectable. */
export function diagnosesFingerprint(
  diagnoses: TreatmentPlanGuidedContext['diagnoses'] | undefined,
): string {
  if (!diagnoses?.length) return '';
  return diagnoses.map((d) => `${d.code}\u0000${d.display}`).join('\u0001');
}

function formatPrimaryDiagnosis(
  context: TreatmentPlanGuidedContext,
): { code: string; display: string } | undefined {
  const primary = context.diagnoses?.find((d) => d.primary) ?? context.diagnoses?.[0];
  if (!primary) return undefined;
  return { code: primary.code, display: primary.display };
}

export function buildIntroMessage(
  context: TreatmentPlanGuidedContext,
  mode: TreatmentPlanGeneratorMode,
): TreatmentPlanGeneratorMessage {
  const primary = formatPrimaryDiagnosis(context);
  if (!primary) {
    return { role: 'assistant', content: DIAGNOSIS_REQUIRED_MESSAGE };
  }
  const knownSymptoms = context.symptoms?.map((s) => s.label).filter(Boolean) ?? [];
  const symptomsClause = knownSymptoms.length
    ? ` I can already see these symptoms documented: ${knownSymptoms.join(', ')}.`
    : '';

  if (mode === 'quick') {
    return {
      role: 'assistant',
      content:
        `Quick generate using diagnosis: ${primary.code} – ${primary.display}.${symptomsClause} `
        + 'I will lean on the intake note and Zoom intake transcript when available, confirm only '
        + 'what is missing (usually 1–3 questions), then draft the plan for your review.',
    };
  }

  return {
    role: 'assistant',
    content:
      `Using diagnosis: ${primary.code} – ${primary.display}.${symptomsClause} Tell me about the client's `
      + 'current priorities and I\'ll suggest goals, objectives, and interventions for you to choose from.',
  };
}

/** Local acknowledgment when the provider edits diagnosis on the form mid-conversation. */
export function buildDiagnosisChangeMessage(
  context: TreatmentPlanGuidedContext,
): TreatmentPlanGeneratorMessage {
  const primary = formatPrimaryDiagnosis(context);
  if (!primary) {
    return { role: 'assistant', content: DIAGNOSIS_REQUIRED_MESSAGE };
  }
  const knownSymptoms = context.symptoms?.map((s) => s.label).filter(Boolean) ?? [];
  const symptomsClause = knownSymptoms.length
    ? ` Documented symptoms I still see: ${knownSymptoms.join(', ')}.`
    : '';
  return {
    role: 'assistant',
    content:
      `Noted — the diagnosis on the form is now ${primary.code} – ${primary.display}. `
      + `I'll use that going forward.${symptomsClause} What are the current priorities for treatment?`,
  };
}

export interface TreatmentPlanGuidedFlowProps {
  context: TreatmentPlanGuidedContext;
  /** Drawer session nonce — scopes the one-shot quick kickoff across StrictMode remounts. */
  launchNonce?: string;
}

export function TreatmentPlanGuidedFlow({ context, launchNonce }: TreatmentPlanGuidedFlowProps): JSX.Element {
  const medplum = useMedplum();
  const {
    patientId,
    episodeId,
    questionnaireId,
    geminiAiDisabled = false,
    onPlanGenerated,
    diagnoses,
    symptoms: formSymptoms,
    operation,
    treatmentFrequency,
  } = context;
  const mode: TreatmentPlanGeneratorMode = context.mode === 'quick' ? 'quick' : 'guided';
  const kickoffKey =
    launchNonce
    ?? `${patientId}|${episodeId ?? ''}|${questionnaireId ?? ''}|${mode}`;

  const hasDiagnosis = Boolean(diagnoses?.length);
  const kickoffStarted = useRef(quickKickoffsStarted.has(kickoffKey));
  const diagnosisRecoveryDone = useRef(hasDiagnosis);
  const lastDiagnosesFingerprint = useRef(diagnosesFingerprint(diagnoses));

  const [messages, setMessages] = useState<TreatmentPlanGeneratorMessage[]>(() => [
    buildIntroMessage(context, mode),
  ]);
  const [checklist, setChecklist] = useState<ChatChecklistPart | undefined>();
  const [guidedSymptoms, setGuidedSymptoms] = useState<EvidenceGroundedSymptom[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState<string | null>(null);
  const [generatedApplied, setGeneratedApplied] = useState(false);
  const [contextOpen, setContextOpen] = useState(false);
  const inFlightRef = useRef(false);
  const latestMessageKey =
    messages.length > 0
      ? `${messages[messages.length - 1].role}:${messages[messages.length - 1].content}`
      : undefined;
  const { viewportRef, latestMessageRef } = useScrollLatestChatToTop(
    messages.length,
    latestMessageKey,
  );

  const mergedSymptoms = mergeEvidenceGroundedSymptoms(formSymptoms, guidedSymptoms);
  const ready = Boolean(episodeId && questionnaireId);
  const canSend =
    ready
    && hasDiagnosis
    && input.trim().length > 0
    && busy === null
    && !generatedApplied
    && !geminiAiDisabled;

  const generatePlan = async (
    conversation: TreatmentPlanGeneratorMessage[],
    symptomsForRequest: EvidenceGroundedSymptom[] = mergedSymptoms,
  ): Promise<void> => {
    if (!episodeId || !questionnaireId) return;
    setBusy('generate');
    try {
      const result = await requestAiTreatmentPlanGenerator(medplum, {
        action: 'generate',
        patientId,
        episodeId,
        questionnaireId,
        messages: conversation,
        operation,
        mode,
        clinicalContext: { diagnoses, symptoms: symptomsForRequest, treatmentFrequency },
      });
      if (result.type === 'needs-information') {
        setMessages([...conversation, { role: 'assistant', content: result.assistantMessage }]);
        return;
      }
      if (result.type !== 'generated') throw new Error('Unexpected response from the AI planner.');
      onPlanGenerated(result);
      setGeneratedApplied(true);
      setMessages([
        ...conversation,
        {
          role: 'assistant',
          content:
            'I have drafted the treatment plan in the form. Review every section, edit anything that '
            + 'needs adjustment, and save or sign when you are ready.',
        },
      ]);
    } catch (err) {
      setError(formatMedplumClientError(err));
    } finally {
      setBusy(null);
    }
  };

  const sendChat = async (
    conversation: TreatmentPlanGeneratorMessage[],
    symptomsForRequest: EvidenceGroundedSymptom[] = mergedSymptoms,
  ): Promise<void> => {
    if (inFlightRef.current || !episodeId || !questionnaireId) return;
    inFlightRef.current = true;
    setBusy('chat');
    setError(null);
    try {
      const result = await requestAiTreatmentPlanGenerator(medplum, {
        action: 'chat',
        patientId,
        episodeId,
        questionnaireId,
        messages: conversation,
        operation,
        mode,
        clinicalContext: { diagnoses, symptoms: symptomsForRequest, treatmentFrequency },
      });
      if (result.type !== 'chat') throw new Error('Unexpected response from the AI planner.');
      const providerTurns = conversation.filter((m) => m.role === 'provider').length;
      // When auto-generate is about to run, skip the intermediate chat bubble so the user sees
      // one assistant reply (the generate result) instead of two similar LLM responses.
      const shouldAutoGenerate =
        result.suggestedReadyToGenerate
        || (mode === 'quick' && providerTurns >= QUICK_MAX_PROVIDER_TURNS);
      if (shouldAutoGenerate) {
        await generatePlan(conversation, symptomsForRequest);
      } else {
        setMessages([...conversation, { role: 'assistant' as const, content: result.assistantMessage }]);
        setChecklist(result.checklist ?? adaptLegacySuggestedResponsesToChecklist(result.suggestedResponses));
      }
    } catch (err) {
      setError(formatMedplumClientError(err));
    } finally {
      inFlightRef.current = false;
      setBusy(null);
    }
  };

  const runQuickKickoff = (): void => {
    if (kickoffStarted.current || quickKickoffsStarted.has(kickoffKey)) return;
    kickoffStarted.current = true;
    quickKickoffsStarted.add(kickoffKey);
    const intro = buildIntroMessage(context, mode);
    const conversation: TreatmentPlanGeneratorMessage[] = [
      intro,
      { role: 'provider', content: QUICK_KICKOFF_MESSAGE },
    ];
    setMessages(conversation);
    setChecklist(undefined);
    void sendChat(conversation);
  };

  useEffect(() => {
    if (geminiAiDisabled || !ready || generatedApplied) return;

    const nextFingerprint = diagnosesFingerprint(diagnoses);
    const prevFingerprint = lastDiagnosesFingerprint.current;
    const diagnosisChanged = nextFingerprint !== prevFingerprint;
    lastDiagnosesFingerprint.current = nextFingerprint;

    // First-time diagnosis: recover from the "select a diagnosis" gate.
    if (hasDiagnosis && !diagnosisRecoveryDone.current) {
      diagnosisRecoveryDone.current = true;
      if (mode === 'quick') {
        runQuickKickoff();
        return;
      }
      setMessages([buildIntroMessage(context, mode)]);
      setChecklist(undefined);
      return;
    }

    // Mid-session form edit (e.g. Z63.0 → F32.x): acknowledge the new form values.
    // Skip the initial mount when fingerprints match and recovery already ran.
    if (diagnosisChanged && diagnosisRecoveryDone.current) {
      if (!hasDiagnosis) {
        diagnosisRecoveryDone.current = false;
        kickoffStarted.current = false;
        quickKickoffsStarted.delete(kickoffKey);
        setChecklist(undefined);
        setMessages((prev) => [...prev, { role: 'assistant', content: DIAGNOSIS_REQUIRED_MESSAGE }]);
        return;
      }
      setChecklist(undefined);
      setMessages((prev) => [...prev, buildDiagnosisChangeMessage(context)]);
      return;
    }

    if (hasDiagnosis && mode === 'quick' && !kickoffStarted.current) {
      runQuickKickoff();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reactive diagnosis recovery + mid-session form edits
  }, [hasDiagnosis, geminiAiDisabled, ready, mode, diagnoses, generatedApplied]);

  const sendProviderMessage = (
    content: string,
    symptomsForRequest: EvidenceGroundedSymptom[] = mergedSymptoms,
  ): void => {
    const trimmed = content.trim();
    if (!trimmed || busy !== null || generatedApplied || !ready || geminiAiDisabled || !hasDiagnosis) {
      return;
    }
    const conversation = [...messages, { role: 'provider' as const, content: trimmed }];
    setMessages(conversation);
    setChecklist(undefined);
    setInput('');
    void sendChat(conversation, symptomsForRequest);
  };

  const handleChecklistSubmit = (submission: ChatChecklistSubmission): void => {
    if (submission.skipped) {
      sendProviderMessage('Skip this question.');
      return;
    }

    const labels = [
      ...submission.selectedLabels,
      ...(submission.otherText?.trim() ? [submission.otherText.trim()] : []),
    ];

    let symptomsForRequest = mergedSymptoms;
    if (checklist?.clinicalField === 'symptoms' && labels.length > 0) {
      const confirmed = symptomsFromGuidedChecklistLabels(labels, {
        checklistId: checklist.id,
        reportedBy: 'provider',
      });
      symptomsForRequest = mergeEvidenceGroundedSymptoms(mergedSymptoms, confirmed);
      setGuidedSymptoms((prev) => mergeEvidenceGroundedSymptoms(prev, confirmed));
    }

    sendProviderMessage(labels.join(', '), symptomsForRequest);
  };

  const handleRetry = (): void => {
    if (busy !== null) return;
    setError(null);
    if (messages[messages.length - 1]?.role === 'provider') void sendChat(messages);
  };

  const handleRestart = (): void => {
    if (busy !== null) return;
    kickoffStarted.current = false;
    diagnosisRecoveryDone.current = hasDiagnosis;
    lastDiagnosesFingerprint.current = diagnosesFingerprint(diagnoses);
    setGuidedSymptoms([]);
    setChecklist(undefined);
    setInput('');
    setError(null);
    setGeneratedApplied(false);
    if (mode === 'quick' && hasDiagnosis && !geminiAiDisabled && ready) {
      runQuickKickoff();
      return;
    }
    setMessages([buildIntroMessage(context, mode)]);
  };

  return (
    <Stack gap={0} h="100%" style={{ minHeight: 0 }}>
      {geminiAiDisabled ? (
        <Text size="sm" c="gray.7" p="md">
          AI documentation assistance is disabled for this provider or client. You can still complete
          the treatment plan manually.
        </Text>
      ) : (
        <>
          {hasDiagnosis && (
            <Box
              px="md"
              pt="sm"
              style={{ borderBottom: '1px solid var(--mantine-color-gray-2)', flexShrink: 0 }}
            >
              <Anchor
                component="button"
                type="button"
                size="xs"
                c="dimmed"
                underline="never"
                onClick={() => setContextOpen((v) => !v)}
              >
                <Group gap={4} wrap="nowrap">
                  {contextOpen ? <IconChevronUp size={12} /> : <IconChevronDown size={12} />}
                  Clinical context in use
                </Group>
              </Anchor>
              <Collapse in={contextOpen}>
                <Stack gap={4} pb="sm">
                  <Text size="xs" c="gray.7">
                    Mode: {mode === 'quick' ? 'Quick generate (documentation-first)' : 'Guided planner'}
                  </Text>
                  {diagnoses?.map((d) => (
                    <Text key={d.code} size="xs" c="gray.7">
                      Diagnosis: {d.code} – {d.display}
                    </Text>
                  ))}
                  {mergedSymptoms.length ? (
                    <Text size="xs" c="gray.7">
                      Symptoms: {mergedSymptoms.map((s) => s.label).join(', ')}
                    </Text>
                  ) : null}
                </Stack>
              </Collapse>
            </Box>
          )}

          <ScrollArea type="auto" viewportRef={viewportRef} style={{ flex: 1, minHeight: 0 }}>
            <Stack gap="md" p="md" role="log" aria-label="Treatment planner conversation">
              {messages.map((message, index) => (
                <ChatMessageBubble
                  key={`${message.role}-${index}`}
                  ref={index === messages.length - 1 ? latestMessageRef : undefined}
                  role={message.role}
                  text={message.content}
                />
              ))}
              {busy !== null && (
                <Group gap="xs" p="xs">
                  <Loader size="xs" />
                  <Text size="xs" c="dimmed">
                    {busy === 'generate'
                      ? 'Generating the treatment plan…'
                      : 'The AI assistant is responding…'}
                  </Text>
                </Group>
              )}
              {generatedApplied && (
                <Group gap={6} wrap="nowrap">
                  <IconCheck size={14} color="var(--mantine-color-green-7)" />
                  <Text size="xs" c="green.8">
                    Draft added to the form for your review.
                  </Text>
                </Group>
              )}
              {!generatedApplied && checklist && busy === null && hasDiagnosis && (
                <ChatChecklistCard
                  key={checklist.id}
                  checklist={checklist}
                  onSubmit={handleChecklistSubmit}
                />
              )}
            </Stack>
          </ScrollArea>

          <Box
            p="md"
            style={{
              borderTop: '1px solid var(--mantine-color-gray-3)',
              background: 'var(--mantine-color-white)',
              flexShrink: 0,
            }}
          >
            {error && (
              <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light" mb="sm">
                <Group justify="space-between" wrap="nowrap">
                  <Text size="xs">{error}</Text>
                  <Button size="compact-xs" variant="subtle" color="red" onClick={handleRetry}>
                    Retry
                  </Button>
                </Group>
              </Alert>
            )}

            {!hasDiagnosis && !generatedApplied && (
              <Alert icon={<IconAlertCircle size={16} />} color="gold" variant="light" mb="sm">
                <Text size="xs">
                  Select a diagnosis in the treatment plan form to continue. The planner will resume
                  automatically.
                </Text>
              </Alert>
            )}

            {generatedApplied ? (
              <Button
                fullWidth
                variant="light"
                color="gray"
                leftSection={<IconRefresh size={16} />}
                onClick={handleRestart}
              >
                {mode === 'quick' ? 'Restart quick generate' : 'Restart guided treatment planner'}
              </Button>
            ) : (
              <Group gap="xs" align="flex-end" wrap="nowrap">
                <Textarea
                  aria-label="Message to the AI assistant"
                  placeholder={
                    hasDiagnosis
                      ? 'Message the AI planner… (Enter to send, Shift+Enter for a new line)'
                      : 'Select a diagnosis in the form to continue…'
                  }
                  value={input}
                  onChange={(e) => setInput(e.currentTarget.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (canSend) sendProviderMessage(input);
                    }
                  }}
                  autosize
                  minRows={2}
                  maxRows={6}
                  disabled={busy !== null || !ready || !hasDiagnosis}
                  style={{ flex: 1 }}
                  styles={{ input: { borderRadius: 10 } }}
                />
                <ActionIcon
                  size="lg"
                  color="gold"
                  variant="filled"
                  onClick={() => sendProviderMessage(input)}
                  disabled={!canSend}
                  loading={busy !== null}
                  aria-label="Send message"
                >
                  <IconSend size={16} />
                </ActionIcon>
              </Group>
            )}
          </Box>
        </>
      )}
    </Stack>
  );
}
